import { useQuery, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/auth-context";

interface LegacyVideo {
  id: string;
  title: string | null;
  prompt: string | null;
  modelId: string;
  status: "pending" | "processing" | "succeeded" | "failed";
  videoUrl: string | null;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  audioUrl: string | null;
  credits: number;
  duration: number;
  aspectRatio: "16:9" | "9:16" | "1:1";
  createdAt: number;
  isLegacy: true;
}

export function useCombinedVideos() {
  // Get user profile to check for supabaseUserId
  const { user } = useAuth();
  const supabaseUserId = user?.supabaseUserId;

  // Native Convex videos (real-time subscription)
  const convexVideos = useQuery(api.videos.listForUser, {});
  const convexLoading = convexVideos === undefined;

  // Legacy videos from Supabase
  const [legacyVideos, setLegacyVideos] = useState<LegacyVideo[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyError, setLegacyError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  const getLegacyVideos = useAction(api.legacyVideos.getMyLegacyVideos);

  const refetchLegacy = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  // Fetch all legacy videos when supabaseUserId is available
  useEffect(() => {
    if (!supabaseUserId) return;

    let cancelled = false;
    setLegacyLoading(true);
    setLegacyError(null);

    // Fetch all legacy videos (no pagination on server side)
    getLegacyVideos({
      limit: 1000, // Fetch all (reasonable upper bound)
      offset: 0,
    })
      .then((videos) => {
        if (!cancelled) {
          setLegacyVideos(videos as LegacyVideo[]);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to fetch legacy videos:", error);
          setLegacyError(error instanceof Error ? error.message : "Failed to load legacy videos");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLegacyLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [supabaseUserId, getLegacyVideos, refetchKey]);

  // Merge and sort by createdAt (newest first)
  const allVideos = useMemo(() => {
    const convexList = convexVideos ?? [];
    // Only show Convex videos that are succeeded (completed)
    const completedConvex = convexList
      .filter((v) => v.status === "succeeded")
      .map((v) => ({ ...v, isLegacy: false as const }));
    const legacy = legacyVideos;
    const combined = [...completedConvex, ...legacy];
    return combined.sort((a, b) => b.createdAt - a.createdAt);
  }, [convexVideos, legacyVideos]);

  // Only consider legacy loading if user has supabaseUserId
  const isLegacyReady = !supabaseUserId || !legacyLoading;

  return {
    videos: allVideos,
    isLoading: convexLoading || !isLegacyReady,
    legacyError,
    refetchLegacy,
  };
}
